import React, { useCallback, useState } from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker'
import styles from './datePickerWtn.module.scss'
import cx from 'classnames'
import { useClickOutside } from 'hooks/use-click-outside'
import moment from 'moment'
import ConfirmationControls from './ConfirmationControls'
import { DateRange } from './types'
import { useDatePickerHeader } from './hooks'

interface RangePickerProps
  extends Omit<ReactDatePickerProps, 'onChange' | 'selected' | 'setSelected' | 'dateFormat'> {
  selectedRange: DateRange
  setSelectedRange: (range: DateRange) => void
  btnCancelText?: string
  btnConfirmText?: string
  inputClassName?: string
  containerClassName?: string
  isMobileFilter?: boolean
  hideButtons?: boolean
  dateFormat?: string
}

const DateRangePickerWTN = ({
  selectedRange,
  setSelectedRange,
  minDate,
  maxDate,
  inputClassName,
  containerClassName,
  btnCancelText,
  btnConfirmText,
  isMobileFilter = false,
  hideButtons = false,
  dateFormat = 'D MMM yyyy',
  ...props
}: RangePickerProps) => {
  const [picked, setPicked] = useState(selectedRange)
  const [isOpenStart, setIsOpenStart] = useState(false)
  const [isOpenEnd, setIsOpenEnd] = useState(false)
  const [isFocusedStart, setIsFocusedStart] = useState(false)
  const [isFocusedEnd, setIsFocusedEnd] = useState(false)
  const isRangeOpen = isOpenStart || isOpenEnd
  const handleCancel = useCallback(() => {
    if (isRangeOpen) {
      setIsOpenStart(false)
      setIsOpenEnd(false)
      if (!hideButtons) setPicked(selectedRange)
    }
  }, [selectedRange, isRangeOpen, hideButtons])

  const confirmSelection = useCallback(
    (selected: DateRange) => {
      setSelectedRange(selected)
      setPicked(selected)
      setIsOpenStart(false)
      setIsOpenEnd(false)
    },
    [setSelectedRange]
  )

  const handleConfirm = useCallback(() => {
    confirmSelection(picked)
  }, [picked, confirmSelection])

  const pickerContainer = useClickOutside<HTMLDivElement>(handleCancel)
  const CustomHeader = useDatePickerHeader({ minDate, maxDate })
  const isConfirmBtnDisabled = !picked.start || !picked.end

  return (
    <div
      ref={pickerContainer}
      onClick={e => {
        if (e.target === e.currentTarget) {
          setIsOpenStart(prev => !prev)
          setIsOpenEnd(false)
        }
      }}
      className={cx(styles.rangePickerInputsContainer, containerClassName, {
        '--is-open': isRangeOpen,
        [styles.commonDateRangePickerInput]: !isMobileFilter,
        [styles.rangePickerInputsContainerMobile]: isMobileFilter,
      })}
    >
      <div
        className={cx(styles.inputContainer, {
          '--is-open': isOpenStart,
          '--is-focused': isFocusedStart,
        })}
      >
        <p className={styles.dateDisplayLabel} aria-hidden>
          {picked.start ? moment(picked.start).format(dateFormat) : ''}
        </p>
        <ReactDatePicker
          {...props}
          minDate={minDate}
          maxDate={maxDate}
          renderCustomHeader={CustomHeader}
          selectsStart
          startDate={picked.start}
          endDate={picked.end}
          selected={picked.start}
          wrapperClassName={cx(styles.pickerWrapperRange, props?.wrapperClassName)}
          className={cx(styles.rangePickerInput, inputClassName, {
            '--is-open': isOpenStart,
          })}
          popperClassName={cx(styles.pickerPopper, props?.popperClassName)}
          showPopperArrow={false}
          popperPlacement='bottom-start'
          onChange={date => {
            const end = date && picked.end && date > picked.end ? date : picked.end
            setPicked({ start: date, end })
            setIsOpenStart(false)
            setIsOpenEnd(true)
          }}
          onInputClick={() => {
            setIsOpenStart(true)
            setIsOpenEnd(false)
          }}
          onFocus={() => {
            setIsFocusedStart(true)
          }}
          onBlur={() => {
            setIsFocusedStart(false)
          }}
          open={isOpenStart}
          readOnly
        >
          {!hideButtons && (
            <ConfirmationControls
              cancelBtn={{ text: btnCancelText, onClick: handleCancel }}
              confirmBtn={{
                text: btnConfirmText,
                onClick: handleConfirm,
                disabled: isConfirmBtnDisabled,
              }}
            />
          )}
        </ReactDatePicker>
      </div>
      <span className={styles.dateSeparator} aria-hidden>
        -
      </span>
      <div
        className={cx(styles.inputContainer, {
          '--is-open': isOpenEnd,
          '--is-focused': isFocusedEnd,
        })}
      >
        <p className={styles.dateDisplayLabel} aria-hidden>
          {picked.end ? moment(picked.end).format(dateFormat) : ''}
        </p>
        <ReactDatePicker
          {...props}
          minDate={minDate}
          maxDate={maxDate}
          renderCustomHeader={CustomHeader}
          selectsEnd
          startDate={picked.start}
          endDate={picked.end}
          selected={picked.end}
          wrapperClassName={cx(styles.pickerWrapperRange, props?.wrapperClassName)}
          className={cx(styles.rangePickerInput, inputClassName, {
            '--is-open': isOpenEnd,
          })}
          popperClassName={cx(styles.pickerPopper, props?.popperClassName)}
          showPopperArrow={false}
          popperPlacement='bottom-start'
          onChange={date => {
            const start = date && picked.start && date < picked.start ? date : picked.start
            const selected = {
              start,
              end: date,
            }

            hideButtons ? confirmSelection(selected) : setPicked(selected)
          }}
          onInputClick={() => {
            setIsOpenStart(false)
            setIsOpenEnd(true)
          }}
          onFocus={() => {
            setIsFocusedEnd(true)
          }}
          onBlur={() => {
            setIsFocusedEnd(false)
          }}
          open={isOpenEnd}
          readOnly
        >
          {!hideButtons && (
            <ConfirmationControls
              cancelBtn={{ text: btnCancelText, onClick: handleCancel }}
              confirmBtn={{
                text: btnConfirmText,
                onClick: handleConfirm,
                disabled: isConfirmBtnDisabled,
              }}
            />
          )}
        </ReactDatePicker>
      </div>
    </div>
  )
}

export default DateRangePickerWTN
